import QtySelector from "./qty-selector/QtySelector";
import Typography from "@mui/material/Typography";
import * as React from "react";
import {Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {IWinnerTicket, IWish, WishStatus, WishType} from "../../../../../store/wish/Helper";
import BuyVouchersDialog from "../../../../dialogs/buy-vouchers-dialog/BuyVouchersDialog";
import {useTranslation} from "react-i18next";
import {RoutePaths} from "../../../../../util/constants";
import {useLocation, useNavigate} from "react-router-dom";
import {RootState, useAppSelector} from "../../../../../store";
import ReactGA from "react-ga4";
import Box from "@mui/material/Box";
import {Swiper as SwiperType} from "swiper";
import TicketService from "../../../../../api/ticketService";
import {WinnerEntry} from "../../../../../util/hooks/useWishCountdown";
import ObtainEntriesDialog from "../../../../dialogs/obtain-entries-dialog/ObtainEntriesDialog";
import {toastError} from "../../../../shared/toast/Toast";
import {getSafeBackendErrorMessage} from "../../../../../util/utils";
import LoadingButton from "@mui/lab/LoadingButton";

interface IProps {
    countDown: null | number
    drawStarted: boolean
    winner: null | WinnerEntry | IWinnerTicket
    wish: IWish
    color: string
    qty: any
    setQty: Dispatch<SetStateAction<any>>
    options: IWish[]
    buttonSize?: 'small' | 'medium' | 'large'
    swiperInstance?: SwiperType
    activeWish?: IWish
    accumulateTotals?: boolean
}

const generateOrderId = (): string => {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let result = "";
    const bytes = new Uint8Array(10);
    crypto.getRandomValues(bytes);
    for (let i = 0; i < 10; i++) {
        // choose index 0–35
        result += alphabet[bytes[i] % alphabet.length];
    }
    return result;
}

const MakeAWishGrid: FC<IProps> = ({buttonSize = 'large', accumulateTotals = false, ...props}) => {
    const isLoggedIn = useAppSelector((state: RootState) => !!state.authReducer.loggedInUser.id)
    const isAdmin = useAppSelector((state: RootState) => state.authReducer.loggedInUser.is_admin)
    const isClient = useAppSelector((state: RootState) => !!state.authReducer.loggedInUser.client_id)
    const {t} = useTranslation()
    const [openBuyVouchers, setOpenBuyVouchers] = useState(false)
    const [openObtainEntries, setOpenObtainEntries] = useState(false)
    const navigate = useNavigate()
    const {pathname} = useLocation()
    const [hasParticipated, setHasParticipated] = useState(false)
    const [loading, setLoading] = useState(false)
    const orderId = useRef(generateOrderId())

    const wishes = useMemo(() => [props.wish, ...(props.wish.children ?? [])], [props.wish]);

    const current = accumulateTotals ? wishes.reduce(
        (accumulator, currentValue) => accumulator + currentValue.tickets_count,
        0,
    ) : props.wish.tickets_count;

    const total = accumulateTotals ? wishes.reduce(
        (accumulator, currentValue) => accumulator + currentValue.max_tickets,
        0,
    ) : props.wish.max_tickets

    useEffect(() => {
        if (props.wish.type === WishType.DRAW && props.wish.cost === 0 && isLoggedIn && !isAdmin && !isClient) {
            TicketService.fetchTicketsForUser(props.wish.id).then(res => {
                setHasParticipated(res.data.length > 0)
            })
        }
        if (!isLoggedIn) {
            setHasParticipated(false)
        }
    }, [props.wish, isLoggedIn, isAdmin, isClient]);

    const handleOpenBuyTickets = useCallback(async () => {
        if (props.wish.status !== WishStatus.PENDING) {
            if (isLoggedIn) {
                ReactGA.event({
                    category: 'Wish',
                    action: 'make_a_wish_click_start',
                    label: `Make a wish - Start - ${props.wish.id}`,
                })
                props.swiperInstance?.autoplay?.stop();
                if (props.wish.type === WishType.VOUCHER) {
                    try {
                        setOpenBuyVouchers(true)
                    } catch (e: any) {
                        toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
                    } finally {
                        setLoading(false)
                    }
                } else {
                    setOpenObtainEntries(true)
                }

                return
            }
            navigate(`${RoutePaths.AUTH}?redirect=${encodeURIComponent(pathname)}`);
        }
    }, [navigate, props.wish.status, props.wish.type, isLoggedIn, props.wish.id, props.swiperInstance?.autoplay, pathname])

    const handleCloseBuyVouchersDialog = async () => {
        setOpenBuyVouchers(false)
        try {
            await TicketService.unreserveVouchers({
                order_id: orderId.current
            })
        } catch (e: any) {
            toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
        }
    }

    const fontVariant = useMemo(() => buttonSize === 'small' ? 'subtitle2' : 'subtitle1', [buttonSize])
    const isRecommended = props.wish.recommended || props.wish.type === WishType.DRAW

    if (props.wish.status === WishStatus.FINISHED) {
        return <Typography variant={fontVariant} fontWeight="600" sx={{color: props.color}} textAlign="center"
                           width="100%">
            {t('wish_has_finished')}
        </Typography>
    }

    if (isAdmin || isClient || !props.countDown || props.countDown < 0 || props.winner) {
        return null
    }

    if (props.drawStarted) {
        return props.wish.type === WishType.DRAW ?
            <Typography variant={fontVariant} fontWeight="600" sx={{color: props.color}} textAlign="center"
                           width="100%">
            {t('await_wish_draw')}
            </Typography> : null
    }

    if (hasParticipated) {
        return <Typography variant={fontVariant} fontWeight="600" sx={{color: props.color}} textAlign="center"
                           width="100%">
            {t('already_participated')}
        </Typography>
    }

    if (current < total) {
        return <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5em',
            width: '100%'
        }}>
            {props.wish.cost !== 0 && (!props.wish.children?.length || !accumulateTotals) &&
                <QtySelector qty={props.qty} setQty={props.setQty}
                             max={total - current}
                             recommended={isRecommended} size={buttonSize}
                             swiperInstance={props.swiperInstance}/>}

            <LoadingButton loading={loading} fullWidth color={isRecommended ? 'tertiary' : 'primary'}
                           variant='contained'
                    onClick={handleOpenBuyTickets} size={buttonSize}
                           sx={{whiteSpace: 'nowrap'}}>{props.wish.type === WishType.DRAW ? t('make_a_wish') : t('buy_voucher', {count: props.qty})}</LoadingButton>
            {props.wish.type === WishType.VOUCHER ? <BuyVouchersDialog open={openBuyVouchers}
                                                                       handleDialogClose={handleCloseBuyVouchersDialog}
                                                                       resetTicketCount={() => props.setQty(1)}
                                                                       wish={props.wish}
                                                                       count={props.qty}
                                                                       setCount={props.setQty}
                                                                       swiperInstance={props.swiperInstance}
                                                                       recommended={isRecommended}
                                                                       setHasParticipated={setHasParticipated}
                                                                       orderId={orderId.current}
                                                                       options={props.options}
                                                                       activeWish={props.activeWish}/> :
                <ObtainEntriesDialog open={openObtainEntries}
                                     handleDialogClose={() => setOpenObtainEntries(false)}
                                    resetTicketCount={() => props.setQty(1)} wish={props.wish} count={props.qty}
                                    setHasParticipated={setHasParticipated}/>}
        </Box>
    }

    return <Typography variant={fontVariant} fontWeight="600" sx={{color: props.color}} textAlign="center"
                       width="100%">
        {props.wish.type === WishType.VOUCHER ? t('all_vouchers_bought') : t('all_entries_obtained')}
    </Typography>

}

export default MakeAWishGrid